/* ==========================================================================
   Virelle Cosmetics — Theme JavaScript
   ========================================================================== */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FREE_SHIPPING_CENTS = 6000;

  /* ---------- Scroll Reveal ---------- */
  function initScrollReveal() {
    if (prefersReducedMotion) {
      document.querySelectorAll('.reveal, .reveal--scale, .reveal--left, .reveal--right').forEach(el => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('.reveal, .reveal--scale, .reveal--left, .reveal--right').forEach(el => observer.observe(el));
  }

  /* ---------- Parallax ---------- */
  function initParallax() {
    if (prefersReducedMotion) return;
    const elements = document.querySelectorAll('[data-parallax]');
    if (!elements.length) return;
    let ticking = false;
    function update() {
      const scrollY = window.scrollY;
      elements.forEach(el => {
        const speed = parseFloat(el.dataset.parallax) || 0.3;
        const rect = el.getBoundingClientRect();
        const offsetTop = scrollY + rect.top;
        const distance = scrollY - offsetTop;
        if (rect.bottom > -200 && rect.top < window.innerHeight + 200) {
          el.style.transform = `translate3d(0, ${distance * speed}px, 0)`;
        }
      });
      ticking = false;
    }
    window.addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
    update();
  }

  /* ---------- Sticky header ---------- */
  function initStickyHeader() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Mobile menu ---------- */
  function initMobileMenu() {
    const toggle = document.querySelector('.menu-toggle');
    const nav = document.querySelector('.header__nav');
    if (!toggle || !nav) return;
    toggle.addEventListener('click', () => {
      const open = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open);
      document.body.style.overflow = open ? 'hidden' : '';
    });
    nav.querySelectorAll('a').forEach(a => {
      a.addEventListener('click', () => { nav.classList.remove('is-open'); toggle.setAttribute('aria-expanded', 'false'); document.body.style.overflow = ''; });
    });
  }

  /* ---------- Nav dropdowns ---------- */
  function initNavDropdowns() {
    const triggers = document.querySelectorAll('.nav-dropdown__trigger');
    function closeAll() {
      triggers.forEach(t => {
        t.setAttribute('aria-expanded', 'false');
        t.nextElementSibling?.classList.remove('is-open');
      });
    }
    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        closeAll();
        if (!isOpen) {
          trigger.setAttribute('aria-expanded', 'true');
          trigger.nextElementSibling?.classList.add('is-open');
        }
      });
    });
    document.addEventListener('click', closeAll);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAll(); });
  }

  /* ---------- Sort Dropdown ---------- */
  function initSortDropdown() {
    document.querySelectorAll('[data-sort-dropdown]').forEach(dd => {
      const trigger = dd.querySelector('.sort-dropdown__trigger');
      const menu = dd.querySelector('.sort-dropdown__menu');
      if (!trigger || !menu) return;
      function close() { trigger.setAttribute('aria-expanded', 'false'); menu.classList.remove('is-open'); }
      function open() { trigger.setAttribute('aria-expanded', 'true'); menu.classList.add('is-open'); }
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        trigger.getAttribute('aria-expanded') === 'true' ? close() : open();
      });
      document.addEventListener('click', close);
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    });
  }

  /* ---------- Product Accordion ---------- */
  function initProductAccordion() {
    document.querySelectorAll('.product-accordion__item').forEach(item => {
      const btn = item.querySelector('.product-accordion__trigger');
      const body = item.querySelector('.product-accordion__body');
      if (!btn || !body) return;
      btn.addEventListener('click', () => {
        const isOpen = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', isOpen);
        body.style.maxHeight = isOpen ? body.scrollHeight + 'px' : '0';
      });
    });
  }

  /* ---------- Cart Drawer ---------- */
  function initCartDrawer() {
    const drawer = document.querySelector('.cart-drawer');
    const overlay = document.querySelector('.cart-overlay');
    const openBtns = document.querySelectorAll('[data-cart-open]');
    const closeBtns = document.querySelectorAll('[data-cart-close]');
    if (!drawer) return;
    function open() { drawer.classList.add('is-open'); if (overlay) overlay.classList.add('is-open'); document.body.style.overflow = 'hidden'; refreshCart(); }
    function close() { drawer.classList.remove('is-open'); if (overlay) overlay.classList.remove('is-open'); document.body.style.overflow = ''; }
    openBtns.forEach(b => b.addEventListener('click', (e) => { e.preventDefault(); open(); }));
    closeBtns.forEach(b => b.addEventListener('click', close));
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('[type="submit"]');
      const originalText = btn ? btn.innerHTML : '';
      if (btn) { btn.disabled = true; btn.innerHTML = 'Adding…'; }
      try {
        const formData = new FormData(form);
        const res = await fetch('/cart/add.js', { method: 'POST', body: formData, headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error('Add to cart failed');
        await res.json(); await refreshCart(); open();
      } catch (err) { console.error(err); alert('Sorry, we couldn\'t add that item. Please try again.'); }
      finally { if (btn) { btn.disabled = false; btn.innerHTML = originalText; } }
    });
  }

  async function refreshCart() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      updateCartCount(cart.item_count);
      renderCartItems(cart);
    } catch (e) { console.error(e); }
  }

  function updateCartCount(count) {
    const badge = document.querySelector('.cart-count');
    if (!badge) return;
    badge.textContent = count;
    badge.style.display = count > 0 ? 'inline-flex' : 'none';
  }

  function renderCartItems(cart) {
    const itemsEl = document.querySelector('.cart-drawer__items');
    const subtotalEl = document.querySelector('.cart-drawer__subtotal-amount');
    const checkoutBtn = document.querySelector('.cart-drawer__checkout');
    const progressFill = document.querySelector('.cart-progress__fill');
    const progressLabel = document.querySelector('.cart-progress__label');
    if (!itemsEl) return;

    if (progressFill && progressLabel) {
      const pct = Math.min(100, (cart.total_price / FREE_SHIPPING_CENTS) * 100);
      progressFill.style.width = pct + '%';
      if (cart.total_price >= FREE_SHIPPING_CENTS) {
        progressLabel.textContent = '🎉 You qualify for free shipping!';
      } else {
        progressLabel.textContent = formatMoney(FREE_SHIPPING_CENTS - cart.total_price) + ' away from free shipping';
      }
    }

    if (cart.item_count === 0) {
      itemsEl.innerHTML = '<div class="cart-empty"><h4>Your cart is empty</h4><p>Discover natural beauty essentials.</p><a href="/collections/all" class="btn btn--outline" data-cart-close>Shop now</a></div>';
      if (checkoutBtn) checkoutBtn.style.display = 'none';
      if (subtotalEl) subtotalEl.textContent = '';
      itemsEl.querySelectorAll('[data-cart-close]').forEach(b => b.addEventListener('click', () => { document.querySelector('.cart-drawer').classList.remove('is-open'); document.querySelector('.cart-overlay')?.classList.remove('is-open'); document.body.style.overflow = ''; }));
      return;
    }

    itemsEl.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-line="${item.key}">
        <img src="${item.image ? item.image.replace(/\.(jpg|jpeg|png|webp|avif)/, '_120x.$1') : ''}" alt="${item.product_title}" class="cart-item__img">
        <div class="cart-item__details">
          <div class="cart-item__title">${item.product_title}</div>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
          <div class="cart-item__row">
            <div class="cart-item__qty">
              <button type="button" class="cart-item__qty-btn" data-item-key="${item.key}" data-delta="-1" aria-label="Decrease quantity">−</button>
              <span>${item.quantity}</span>
              <button type="button" class="cart-item__qty-btn" data-item-key="${item.key}" data-delta="1" aria-label="Increase quantity">+</button>
            </div>
            <div class="cart-item__line-price">${formatMoney(item.final_line_price)}</div>
          </div>
          <button type="button" class="cart-item__remove" data-item-key="${item.key}">Remove</button>
        </div>
      </div>
    `).join('');

    itemsEl.querySelectorAll('.cart-item__qty-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.itemKey;
        const delta = parseInt(btn.dataset.delta, 10);
        const item = cart.items.find(i => i.key === key);
        if (!item) return;
        const newQty = Math.max(0, item.quantity + delta);
        btn.disabled = true;
        try {
          await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity: newQty }) });
          await refreshCart();
        } catch (err) { console.error(err); btn.disabled = false; }
      });
    });

    itemsEl.querySelectorAll('.cart-item__remove').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.itemKey;
        btn.disabled = true;
        try {
          await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity: 0 }) });
          await refreshCart();
        } catch (err) { console.error(err); btn.disabled = false; }
      });
    });

    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    if (checkoutBtn) checkoutBtn.style.display = 'inline-flex';
  }

  function formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    const symbol = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD';
    return `$${value} ${symbol}`;
  }

  /* ---------- Buy Now ---------- */
  function initBuyNow() {
    const btn = document.getElementById('buy-now-btn');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      const form = document.getElementById('product-form');
      if (!form) return;
      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Processing…';
      try {
        const formData = new FormData(form);
        const res = await fetch('/cart/add.js', { method: 'POST', body: formData, headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('Add failed');
        window.location.href = '/checkout';
      } catch (err) {
        console.error(err);
        btn.disabled = false;
        btn.textContent = original;
      }
    });
  }

  /* ---------- Product gallery ---------- */
  function initProductGallery() {
    const main = document.querySelector('.product__main-image img');
    const thumbs = document.querySelectorAll('.product__thumb');
    if (!main || !thumbs.length) return;
    thumbs.forEach(t => {
      t.addEventListener('click', () => {
        const url = t.dataset.image;
        if (url) { main.style.opacity = '0'; setTimeout(() => { main.src = url; main.style.opacity = '1'; }, 200); }
        thumbs.forEach(x => x.classList.remove('is-active')); t.classList.add('is-active');
      });
    });
    main.style.transition = 'opacity 0.3s ease';
  }

  /* ---------- Quantity selectors ---------- */
  function initQuantity() {
    document.querySelectorAll('.product__quantity').forEach(wrap => {
      const input = wrap.querySelector('input');
      const minus = wrap.querySelector('[data-qty-minus]');
      const plus = wrap.querySelector('[data-qty-plus]');
      if (!input) return;
      minus?.addEventListener('click', () => { input.value = Math.max(1, parseInt(input.value, 10) - 1); });
      plus?.addEventListener('click', () => { input.value = parseInt(input.value, 10) + 1; });
    });
  }

  /* ---------- Variant selector ---------- */
  function initVariantSelector() {
    document.querySelectorAll('[data-variant-options]').forEach(group => {
      const options = group.querySelectorAll('.product__variant-option');
      const idInput = document.querySelector('[name="id"]');
      options.forEach(opt => {
        opt.addEventListener('click', () => {
          options.forEach(o => o.classList.remove('is-active')); opt.classList.add('is-active');
          if (idInput && opt.dataset.variantId) idInput.value = opt.dataset.variantId;
          if (opt.dataset.price) { const priceEl = document.querySelector('.product__price-current'); if (priceEl) priceEl.textContent = opt.dataset.price; }
        });
      });
    });
  }

  /* ---------- Newsletter ---------- */
  function initNewsletter() {
    document.querySelectorAll('.newsletter__form').forEach(form => {
      form.addEventListener('submit', () => { const btn = form.querySelector('button'); if (btn) { btn.innerHTML = 'Thanks!'; btn.disabled = true; } });
    });
  }

  /* ---------- Sticky Add to Cart ---------- */
  function initStickyAtc() {
    const bar = document.getElementById('sticky-atc');
    const mainBtn = document.getElementById('main-atc');
    const stickyBtn = document.getElementById('sticky-atc-btn');
    if (!bar || !mainBtn) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const visible = !entry.isIntersecting;
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });
    }, { threshold: 0, rootMargin: '-80px 0px 0px 0px' });
    observer.observe(mainBtn);
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        const form = document.getElementById('product-form');
        if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });
    }
  }

  /* ---------- Quick add ---------- */
  function initQuickAdd() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();
      const variantId = btn.dataset.quickAdd;
      if (!variantId) return;
      const original = btn.textContent;
      btn.disabled = true; btn.textContent = 'Adding…';
      try {
        await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ id: variantId, quantity: 1 }) });
        await refreshCart();
        document.querySelector('.cart-drawer')?.classList.add('is-open');
        document.querySelector('.cart-overlay')?.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      } catch (err) { console.error(err); }
      finally { btn.disabled = false; btn.textContent = original; }
    });
  }

  /* ---------- Init ---------- */
  function init() {
    initScrollReveal();
    initParallax();
    initStickyHeader();
    initMobileMenu();
    initNavDropdowns();
    initSortDropdown();
    initProductAccordion();
    initCartDrawer();
    initProductGallery();
    initQuantity();
    initVariantSelector();
    initNewsletter();
    initBuyNow();
    initQuickAdd();
    initStickyAtc();
    refreshCart();
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); } else { init(); }
})();
